import React from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CATEGORIES } from "../../config/offeringCatalog";
import { CategoryFilter, GroupBy } from "../../hooks/useStageFeatureMatrix";

interface MatrixToolbarProps {
  groupBy: GroupBy;
  onGroupByChange: (groupBy: GroupBy) => void;
  category: CategoryFilter;
  onCategoryChange: (category: CategoryFilter) => void;
}

const pillClasses = (active: boolean) =>
  `rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
    active
      ? "border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-slate-900"
      : "border-gray-200 bg-white text-gray-500 hover:text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-400 dark:hover:text-white"
  }`;

const MatrixToolbar: React.FC<MatrixToolbarProps> = ({
  groupBy,
  onGroupByChange,
  category,
  onCategoryChange,
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="mr-1 text-xs text-gray-500 dark:text-gray-400">
      Group by
    </span>
    <ToggleGroup
      aria-label="Group by"
      className="rounded-full border border-gray-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
      onValueChange={(value) => {
        // Radix emits "" when the active item is clicked again; keep the selection.
        if (value === "category" || value === "stage") {
          onGroupByChange(value);
        }
      }}
      type="single"
      value={groupBy}
    >
      {(["category", "stage"] as const).map((value) => (
        <ToggleGroupItem
          className="h-7 rounded-full px-3.5 text-xs font-medium text-gray-500 data-[state=on]:bg-gray-900 data-[state=on]:text-white dark:text-gray-400 dark:data-[state=on]:bg-white dark:data-[state=on]:text-slate-900"
          key={value}
          value={value}
        >
          {value === "category" ? "Category" : "Stage"}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>

    <span
      aria-hidden="true"
      className="mx-1.5 h-5 w-px bg-gray-200 dark:bg-slate-700"
    />

    {(["All", ...CATEGORIES] as const).map((value) => (
      <button
        aria-pressed={category === value}
        className={pillClasses(category === value)}
        key={value}
        onClick={() => onCategoryChange(value)}
        type="button"
      >
        {value === "All" ? "All categories" : value}
      </button>
    ))}
  </div>
);

export default MatrixToolbar;
